import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATTER_TYPES = [
  'Divorce / marital asset disclosure',
  'Estate / probate',
  'Bankruptcy',
  'Fiduciary review',
  'Business dispute',
  'Tax / compliance support',
  'Insurance / claims support',
  'Other',
];

export default function NewMatter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [form, setForm] = useState({
    title: '',
    matter_type: '',
    client_name: '',
    client_email: '',
    description: '',
    opposing_party: '',
    court_reference: '',
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acknowledged) {
      toast.error('Please acknowledge the terms before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/portal/matters`, form, { withCredentials: true });
      toast.success(`Matter ${res.data.matter_id} created`);
      navigate(`/portal/matters/${res.data.matter_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create matter');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="new-matter-page">
      <nav className="bg-white border-b border-[#D4D4D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/portal" className="flex items-center gap-3">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8] hidden sm:inline">Secure Matter Portal</span>
          </Link>
          <Link to="/portal" className="text-sm text-[#64748B] hover:text-[#0B1F3F] flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 md:px-12 py-10">
        <div className="space-y-2 mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">New Matter</p>
          <h1 className="font-heading text-3xl text-[#0B1F3F]" data-testid="new-matter-h1">Create a Matter</h1>
          <p className="text-sm text-[#64748B]">Submit matter details to begin the intake process.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-[#D4D4D0] rounded-sm p-8" data-testid="new-matter-form">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Matter Title *</Label>
            <Input
              data-testid="matter-title-input"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Smith Digital Asset Disclosure"
              className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Matter Type *</Label>
            <select
              data-testid="matter-type-select"
              value={form.matter_type}
              onChange={(e) => handleChange('matter_type', e.target.value)}
              className="w-full h-12 px-3 bg-white border border-[#D4D4D0] rounded-md text-sm text-[#0B1F3F] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F] focus:outline-none"
              required
            >
              <option value="" disabled>Select matter type</option>
              {MATTER_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Client Name *</Label>
              <Input
                data-testid="client-name-input"
                value={form.client_name}
                onChange={(e) => handleChange('client_name', e.target.value)}
                className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Client Email</Label>
              <Input
                data-testid="client-email-input"
                type="email"
                value={form.client_email}
                onChange={(e) => handleChange('client_email', e.target.value)}
                className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Opposing Party</Label>
              <Input
                value={form.opposing_party}
                onChange={(e) => handleChange('opposing_party', e.target.value)}
                className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Court / Case Reference</Label>
              <Input
                value={form.court_reference}
                onChange={(e) => handleChange('court_reference', e.target.value)}
                className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Description</Label>
            <Textarea
              data-testid="matter-description-input"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the matter and the record set involved."
              className="min-h-[120px] bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
            />
          </div>

          <Separator className="bg-[#D4D4D0]" />

          {/* Legal Acknowledgments */}
          <div className="space-y-3 bg-[#F5F3EF] border border-[#D4D4D0] rounded-sm p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Acknowledgments</p>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="acknowledge"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-[#D4D4D0]"
                data-testid="acknowledge-checkbox"
              />
              <label htmlFor="acknowledge" className="text-xs text-[#64748B] leading-relaxed cursor-pointer">
                I acknowledge that VARfacts provides record-based evaluation and verification-status reporting only. VARfacts does not provide legal, tax, or investment advice, does not determine ownership, and does not provide custody services. All findings are limited to the produced record set.
              </label>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || !acknowledged}
            className="btn-primary-varfacts px-8 py-3 rounded-sm h-12 w-full"
            data-testid="create-matter-btn"
          >
            {submitting ? 'Creating...' : 'Create Matter'}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
